import { Stack, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CrudPage } from '../../shared/components/CrudPage';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { BrandTreeSelect } from './BrandTreeSelect';
import { resources } from '../../core/services/resources';
import type { Organization, Product } from '../../core/types/data-models';

/**
 * Products are leaves of the Organization → Brand → Sub-brand → Product
 * hierarchy. They now live in their own `/api/v1/products` table (no
 * longer a Brand row), but still link to a parent brand and an owning
 * organization the same way.
 */
export function ProductsPage() {
  // Shared with EntitySelect for parent-brand resolution + table name cell.
  const brandsLookup = useQuery({
    queryKey: ['brands', 'select'],
    queryFn: () => resources.brands.list({ pageSize: 200, sort: 'name' }),
  });
  const organizationsLookup = useQuery({
    queryKey: ['organizations', 'select'],
    queryFn: () => resources.organizations.list({ pageSize: 200, sort: 'name' }),
  });

  const brandName = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of brandsLookup.data?.data ?? []) m.set(b.id, b.name);
    return m;
  }, [brandsLookup.data]);
  const orgName = useMemo(() => {
    const m = new Map<number, string>();
    for (const o of organizationsLookup.data?.data ?? []) m.set(o.id, o.name);
    return m;
  }, [organizationsLookup.data]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
      {
        field: 'parentBrandId',
        headerName: 'Parent brand',
        width: 200,
        renderCell: (p) => {
          const id = p.value as number | null | undefined;
          return id == null ? '' : brandName.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'organizationId',
        headerName: 'Organization',
        width: 200,
        renderCell: (p) => {
          const id = p.value as number | null | undefined;
          return id == null ? '' : orgName.get(id) ?? `#${id}`;
        },
      },
    ],
    [brandName, orgName],
  );

  return (
    <CrudPage<Product>
      title="Products"
      description="Leaf items in the Organization → Brand → Sub-brand → Product hierarchy."
      cacheKey={['products']}
      service={resources.products}
      columns={columns}
      defaultValues={{ name: '', parentBrandId: null, organizationId: null }}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          <TextField
            label="Name"
            required
            value={values.name ?? ''}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
          />
          <BrandTreeSelect
            label="Parent brand"
            required
            // Filter by the selected organization so a product is always
            // assigned to a brand within the same org.
            organizationId={values.organizationId}
            helperText={
              values.organizationId != null
                ? 'Showing brands of the selected organization (indented by hierarchy).'
                : 'The brand this product belongs to. Picking a parent inherits its organization.'
            }
            value={values.parentBrandId}
            onChange={(id) => {
              if (id == null) {
                setValues({ ...values, parentBrandId: null, organizationId: null });
                return;
              }
              const parent = brandsLookup.data?.data.find((b) => b.id === id);
              setValues({
                ...values,
                parentBrandId: id,
                organizationId: parent?.organizationId ?? null,
              });
            }}
          />
          <EntitySelect<Organization>
            cacheKey={['organizations', 'select']}
            service={resources.organizations}
            label="Organization"
            helperText={
              values.parentBrandId != null
                ? 'Inherited from the parent brand.'
                : 'Optional. The organization that owns this product.'
            }
            value={values.organizationId}
            onChange={(id) => setValues({ ...values, organizationId: id })}
            disabled={values.parentBrandId != null}
          />
        </Stack>
      )}
    />
  );
}
